import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreTranslationService } from '@core/services/translation.service';
import { locale as english } from 'app/shared/i18n/en';
import { locale as tamil } from 'app/shared/i18n/ta';
import { TranslateService } from '@ngx-translate/core';

import { SLSService } from '@core/services/secure-localstorage.service';
import { ApiRouteConstants } from 'app/shared/constants/api.constants';
import { User, Permission } from 'app/shared/models';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { Helper } from '@core/util/helper';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  @BlockUI() blockUI: NgBlockUI;
  //public
  public currentUser: Observable<User>;

  //private
  private currentUserSubject: BehaviorSubject<User>;

  /**
   *
   * @param {HttpClient} _http
   */
  constructor(
    private _http: HttpClient,
    private _coreTranslationService: CoreTranslationService,
    private _translate: TranslateService
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(SLSService.getValueByKey('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
    this._coreTranslationService.translate(english, tamil);
  }

  // getter: currentUserValue
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  get isProducer() {
    return this.currentUser && this.currentUserSubject.value?.partnerType === 'producer';
  }

  get isStaff(): boolean {
    return this.currentUserSubject.value?.loginType === 'staff';
  }

  /** Effective permissions: merged store (partner) roles + staff permissions when staff login — matches API RolesGuard */
  get effectivePermissions(): string[] {
    return Helper.effectiveUserPermissions(this.currentUserSubject.value);
  }

  /** Backward compatible portal access check during migration. */
  private hasPortalAccessPermission(perms: string[]): boolean {
    return perms.includes(Permission.PortalAccess) || perms.includes('portal-admin');
  }

  /** Partner accounts that must set a new password before using the portal. */
  requiresPasswordChange(user?: User | null): boolean {
    if (!user || user.loginType === 'staff') {
      return false;
    }
    return user.mustChangePassword === true;
  }

  clearMustChangePassword(): void {
    const user = this.currentUserSubject.value;
    if (!user) {
      return;
    }
    const updated = { ...user, mustChangePassword: false };
    SLSService.setKeyValue('currentUser', JSON.stringify(updated));
    this.currentUserSubject.next(updated);
  }

  private applyMustChangePassword(user: User, data: any): User {
    const flag =
      data?.mustChangePassword === true ||
      data?.partner?.mustChangePassword === true ||
      user.mustChangePassword === true;
    return { ...user, mustChangePassword: flag };
  }

  /**
   * Login with type (partner or staff). Returns and stores partner + optional staff tokens.
   */
  loginWithType(loginType: 'partner' | 'staff', countryCode: string, mobileNumber: string, password: string) {
    return this._http.post<any>(ApiRouteConstants.AUTHENTICATION.LOGIN, {
      loginType,
      countryCode: countryCode || undefined,
      mobileNumber: mobileNumber || undefined,
      password,
    }).pipe(
      map((response) => {
        if (response && response.status === 'OK' && response.results) {
          const data = response.results;
          const partnerToken = data.partnerToken || data.accessToken || data.storeToken;
          const partner = data.partner || {};
          const currentUser: User = this.applyMustChangePassword({
            ...partner,
            accessToken: partnerToken,
            partnerToken,
            staffToken: data.staffToken,
            loginType: data.loginType,
            staff: data.staff,
          } as User, data);
          if (!partnerToken) {
            throw new Error('Missing store token. Contact Support!');
          }
          if (data.loginType === 'staff') {
            if (!data.staff || !data.staffToken) {
              throw new Error('Access Restricted. Contact Support!');
            }
          } else {
            const perms = Helper.collatePermissions(partner.roles || []);
            if (!this.hasPortalAccessPermission(perms)) {
              throw new Error('Access Restricted. Contact Support!');
            }
          }
          SLSService.setKeyValue('currentUser', JSON.stringify(currentUser));
          this.currentUserSubject.next(currentUser);
          return currentUser;
        } else {
          throw new Error(response?.message || 'Error Occurred. Contact Support!');
        }
      })
    );
  }

  /**
   * User login (legacy partner login check)
   */
  loginCheck(countryCode: string, mobileNumber: string, password: string) {
    return this._http.post<any>(ApiRouteConstants.AUTHENTICATION.LOGIN_CHECK, { countryCode, mobileNumber, password }).pipe(
      map((response) => {
        if (response && response.status === 'OK') {
          return response.results;
        } else {
          throw new Error('Error Occurred. Contact Support!');
        }
      })
    );
  }

  /**
   * User login
   *
   * @param phoneCode
   * @param mobile
   * @param otpCode
   * @returns member
   */
  otpCheck(countryCode: string, mobileNumber: string, password: string, otpCode: string) {
    return this._http.post<any>(ApiRouteConstants.AUTHENTICATION.OTP_CHECK, { countryCode, mobileNumber, password, otpCode }).pipe(
      map((user) => {
        // login successful if there's a jwt token in the response
        if (user && user.results.length > 0 && user.results[0].accessToken) {
          const rawDetails = user.results[0];
          const userPermissions = Helper.collatePermissions(rawDetails.roles);
          if (this.hasPortalAccessPermission(userPermissions)) {
            const userDetails = this.applyMustChangePassword(rawDetails as User, rawDetails);
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            SLSService.setKeyValue('currentUser', JSON.stringify(userDetails));
            // notify
            this.currentUserSubject.next(userDetails);
          } else {
            throw new Error('Access Restricted. Contact Support!');
          }
        } else {
          throw new Error('Error Occurred. Contact Support!');
        }
      })
    );
  }

  /**
   * User logout
   *
   */
  logout() {
    // remove user from local storage to log user out
    SLSService.removeKey('currentUser');
    // notify
    this.currentUserSubject.next(null);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
}
